import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { getInventory } from '../../services/inventoryService';
import { getLocations } from '../../services/locationService';

// Material UI components
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Tooltip,
  Divider,
  InputAdornment,
  Skeleton,
  Alert,
  useTheme
} from '@mui/material';

// Material UI icons
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Inventory as InventoryIcon,
  Clear as ClearIcon
} from '@mui/icons-material';

const InventoryList = () => {
  const theme = useTheme();
  const location = useLocation();
  const [inventory, setInventory] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state
  const [page, setPage] = useState(1); // Changed from 0 to 1 for API pagination
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Filter state
  const [filters, setFilters] = useState({
    location_id: '',
    search: '',
    showLowStock: location.state?.showLowStock || false
  });

  // Debounced search state
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [searchInputValue, setSearchInputValue] = useState(filters.search);

  // Expanded filter state
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Effect for debouncing search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchInputValue);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchInputValue]);

  // Main data fetching effect
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch locations for filter dropdown
        const locationsData = await getLocations();
        setLocations(locationsData);

        // Fetch inventory data with pagination and server-side filtering
        const response = await getInventory({
          location_id: filters.location_id || undefined,
          search: debouncedSearchTerm || undefined,
          low_stock: filters.showLowStock || undefined,
          page: page,
          limit: rowsPerPage
        });

        console.log('Inventory API response:', response);

        // Update state with paginated data and metadata
        setInventory(response.items || []);
        setTotalItems(response.pagination?.total || 0);
        setTotalPages(response.pagination?.totalPages || 0);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching inventory data:', err);
        setError('Failed to load inventory data. Please try again.');
        setLoading(false);
      }
    };

    fetchData();
  }, [filters.location_id, debouncedSearchTerm, filters.showLowStock, page, rowsPerPage]);

  // All filtering is now done server-side, so we can use the inventory directly
  const filteredInventory = inventory;

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    // Material UI TablePagination uses 0-based indexing, but our API uses 1-based indexing
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1); // Reset to first page when changing rows per page
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;

    // Update the search input value (this will be debounced)
    setSearchInputValue(value);

    // Update the filters state for UI display only
    setFilters({
      ...filters,
      search: value
    });
  };

  // Reset page when debounced search term changes
  useEffect(() => {
    // Reset to first page when search changes
    setPage(1);
  }, [debouncedSearchTerm]);

  const handleLocationChange = (e) => {
    setFilters({
      ...filters,
      location_id: e.target.value
    });
    setPage(1); // Reset to first page when location filter changes
  };

  const handleLowStockChange = (e) => {
    setFilters({
      ...filters,
      showLowStock: e.target.checked
    });
    setPage(1); // Reset to first page when low stock filter changes
  };

  const handleClearFilters = () => {
    setFilters({
      location_id: '',
      search: '',
      showLowStock: false
    });
    setSearchInputValue('');
    setDebouncedSearchTerm('');
    setPage(1); // Reset to first page when clearing filters
  };

  const toggleFiltersExpanded = () => {
    setFiltersExpanded(!filtersExpanded);
  };

  // Get status chip color and icon
  const getStatusChip = (item) => {
    // If location is null, it means the part has no inventory yet
    if (!item.location) {
      return (
        <Chip
          icon={<WarningIcon />}
          label="No Inventory"
          color="warning"
          size="small"
          variant="outlined"
        />
      );
    }

    const isLowStock = item.part.reorder_point !== null && item.quantity <= item.part.reorder_point;

    return (
      <Chip
        icon={isLowStock ? <WarningIcon /> : <CheckCircleIcon />}
        label={isLowStock ? "Low Stock" : "In Stock"}
        color={isLowStock ? "error" : "success"}
        size="small"
        variant={isLowStock ? "filled" : "outlined"}
      />
    );
  };

  // Render loading skeletons
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Inventory Management
        </Typography>

        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Skeleton variant="rectangular" height={56} />
            </Grid>
            <Grid item xs={12} md={4}>
              <Skeleton variant="rectangular" height={56} />
            </Grid>
            <Grid item xs={12} md={4}>
              <Skeleton variant="rectangular" height={56} />
            </Grid>
          </Grid>
        </Paper>

        <Paper>
          <Skeleton variant="rectangular" height={400} />
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Inventory Management
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => window.location.reload()}
            sx={{ mr: 1 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            component={RouterLink}
            to="/inventory/new"
          >
            Add New Part
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper elevation={2} sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{
          p: 2,
          bgcolor: 'grey.50',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${theme.palette.divider}`
        }}>
          <Typography variant="h6">
            Filters
          </Typography>
          <Box>
            <Button
              size="small"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              sx={{ mr: 1 }}
              disabled={!filters.search && !filters.location_id && !filters.showLowStock}
            >
              Clear
            </Button>
            <Button
              size="small"
              startIcon={<FilterListIcon />}
              onClick={toggleFiltersExpanded}
            >
              {filtersExpanded ? 'Less Filters' : 'More Filters'}
            </Button>
          </Box>
        </Box>

        <Box sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Search"
                placeholder="Search by part number or description"
                value={filters.search}
                onChange={handleSearchChange}
                variant="outlined"
                // Using startAdornment directly instead of deprecated InputProps
                startAdornment={
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                }
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="location-filter-label">Location</InputLabel>
                <Select
                  labelId="location-filter-label"
                  id="location-filter"
                  value={filters.location_id}
                  onChange={handleLocationChange}
                  label="Location"
                  sx={{ minWidth: '200px' }}
                >
                  <MenuItem value="">All Locations</MenuItem>
                  {locations.map(location => (
                    <MenuItem key={location.id} value={location.id}>
                      {location.warehouse.name} - {location.rack}-{location.shelf}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.showLowStock}
                    onChange={handleLowStockChange}
                    color="warning"
                  />
                }
                label="Low Stock Only"
                sx={{ height: '100%', display: 'flex' }}
              />
            </Grid>
          </Grid>

          {filtersExpanded && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                Additional Filters (Coming Soon)
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Part Type"
                    select
                    disabled
                    variant="outlined"
                    helperText="Filter by part type"
                  >
                    <MenuItem value="">All Types</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Supplier"
                    select
                    disabled
                    variant="outlined"
                    helperText="Filter by supplier"
                  >
                    <MenuItem value="">All Suppliers</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Date Added"
                    type="date"
                    disabled
                    variant="outlined"
                    helperText="Filter by date added"
                    // Using slotProps instead of deprecated InputLabelProps
                    slotProps={{
                      inputLabel: { shrink: true }
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>

        <Box sx={{
          p: 2,
          bgcolor: 'grey.50',
          borderTop: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="body2" color="text.secondary">
            {totalItems} items found
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              color="primary"
              component={RouterLink}
              to="/inventory/adjust"
              startIcon={<EditIcon />}
            >
              Adjust Inventory
            </Button>
            <Button
              variant="contained"
              color="primary"
              component={RouterLink}
              to="/inventory/physical/list"
              startIcon={<InventoryIcon />}
            >
              Physical Inventory
            </Button>
            <Button
              variant="contained"
              color="secondary"
              component={RouterLink}
              to="/inventory/reorder-bulk"
              startIcon={<FilterListIcon />}
            >
              Manage Reorder Points
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Inventory Table */}
      {inventory.length === 0 ? (
        <Paper
          elevation={2}
          sx={{
            p: 4,
            borderRadius: 2,
            textAlign: 'center',
            bgcolor: 'background.paper'
          }}
        >
          <InventoryIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No inventory items found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {totalItems === 0
              ? "No inventory items exist in the database or match your location filter."
              : "No inventory items found on this page. Try changing page or filters."}
          </Typography>
          <Button
            variant="outlined"
            onClick={handleClearFilters}
            startIcon={<ClearIcon />}
          >
            Clear Filters
          </Button>
        </Paper>
      ) : filteredInventory.length === 0 ? (
        <Paper
          elevation={2}
          sx={{
            p: 4,
            borderRadius: 2,
            textAlign: 'center',
            bgcolor: 'background.paper'
          }}
        >
          <InventoryIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No matching items
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Items exist in the database but none match your search or low stock filter.
          </Typography>
          <Button
            variant="outlined"
            onClick={handleClearFilters}
            startIcon={<ClearIcon />}
          >
            Clear Filters
          </Button>
        </Paper>
      ) : (
        <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <TableContainer>
            <Table size="medium">
              <TableHead>
                <TableRow>
                  <TableCell>Part Number</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell align="right">Quantity</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredInventory.map(item => (
                    <TableRow
                      key={item.id}
                      hover
                      sx={{
                        '&:last-child td, &:last-child th': { border: 0 },
                        ...(item.part.reorder_point !== null && item.quantity <= item.part.reorder_point && {
                          bgcolor: 'error.lighter',
                          '&:hover': {
                            bgcolor: 'error.100',
                          }
                        })
                      }}
                    >
                      <TableCell component="th" scope="row">
                        <Typography variant="body2" fontWeight="medium">
                          {item.part.internal_part_number}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
                          {item.part.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {item.location ?
                            `${item.location.warehouse.name} - ${item.location.rack}-${item.location.shelf}` :
                            "No location assigned"}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          fontWeight="medium"
                          color={
                            !item.location ? 'warning.main' :
                            (item.part.reorder_point !== null && item.quantity <= item.part.reorder_point)
                              ? 'error.main'
                              : 'text.primary'
                          }
                        >
                          {item.location ? item.quantity : '0'}
                        </Typography>
                      </TableCell>
                      <TableCell>{getStatusChip(item)}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Details">
                          <Button
                            component={RouterLink}
                            to={`/inventory/${item.part.id}`}
                            variant="outlined"
                            size="small"
                          >
                            Details
                          </Button>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Page {page} of {totalPages}
            </Typography>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={totalItems}
              rowsPerPage={rowsPerPage}
              page={page - 1} // Convert 1-based to 0-based for Material UI
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Box>
        </Paper>
      )}
    </Container>
  );
};

export default InventoryList;
